import 'bootstrap/dist/css/bootstrap.min.css'
import {sdk} from './sdk.js';
import {sdkLocal} from './sdkLocal.js';
import './Tiktok.css';
import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import LiberiumHead from './LiberiumHead';
import { TiktokVideo } from "./TiktokVideo";
import { VastAdPlayer } from "./VastAdPlayer";
import { TiktokInterstitialAd } from "./TiktokInterstitialAd";
import { useAdManager } from "./VastAdManager";
import { useNavigate } from './Router';
import { FaArrowLeft } from 'react-icons/fa';

const Tiktok = () => {
    const navigate = useNavigate();
    const [user, setUser] = useState(null);
    const [posts, setPosts] = useState([]);
    const [ids, setIds] = useState([]);
    const [lastId, setLastId] = useState(null);
    const [loading, setLoading] = useState(false);
    const [contentItems, setContentItems] = useState([]);
    const observerRef = useRef();
    const videoContainerRef = useRef(null);
    
    // État pour tracker la visibilité de chaque élément
    const [visibilityMap, setVisibilityMap] = useState(new Map());
    const visibilityObserversRef = useRef(new Map());

    // Gestionnaire de publicités
    const adManager = useAdManager();

    // État pour gérer les statistiques des fallbacks
    const [fallbackStats, setFallbackStats] = useState({
        totalFallbacks: 0,
        successfulFallbacks: 0,
        lastFallbackTime: null
    });

    // Initialisation de l'utilisateur
    useEffect(() => {
        async function fetchUser() {
            try {
                const myself = await sdk.models.SocialUser.getMyself({});
                if (typeof myself !== "undefined" && myself !== null && typeof myself.error === "undefined") {
                    setUser(myself);
                    localStorage.setItem("myself", JSON.stringify(myself));
                }
            } catch (error) {
                console.error("Error fetching user:", error);
            }
        }

        const token = localStorage.getItem("liberium-token");
        if (token !== null) {
            const mine = localStorage.getItem("myself");
            if (mine !== null) {
                try {
                    setUser(JSON.parse(mine));
                } catch (e) {
                    fetchUser();
                }
            } else {
                fetchUser();
            }
        }
    }, []);

    // Observer la visibilité de chaque élément individuellement
    const observeElementVisibility = useCallback((element, key) => {
        if (!element || visibilityObserversRef.current.has(key)) return;

        const observer = new IntersectionObserver(
            (entries) => {
                const [entry] = entries;
                const isVisible = entry.isIntersecting && entry.intersectionRatio >= 0.7;
                
                setVisibilityMap(prev => {
                    const newMap = new Map(prev);
                    newMap.set(key, isVisible);
                    return newMap;
                });
            },
            {
                root: null,
                rootMargin: '0px',
                threshold: [0, 0.1, 0.25, 0.5, 0.7, 0.9, 1.0]
            }
        );

        observer.observe(element);
        visibilityObserversRef.current.set(key, observer);
    }, []);

    // Nettoyer les observers
    const cleanupObserver = useCallback((key) => {
        const observer = visibilityObserversRef.current.get(key);
        if (observer) {
            observer.disconnect();
            visibilityObserversRef.current.delete(key);
        }
        
        setVisibilityMap(prev => {
            const newMap = new Map(prev);
            newMap.delete(key);
            return newMap;
        });
    }, []);

    // Récupérer les vidéos
    const getVideos = useCallback(async () => {
        if (loading) return;
        
        setLoading(true);
        try {
            const newVideos = await sdk.models.post.getVideos({ last_id: lastId, ids: ids });
            if (newVideos && newVideos.length > 0) {
                setPosts((oldVideos) => [...oldVideos, ...newVideos]);
                
                const newIds = newVideos.map(vid => vid.id);
                setIds((oldIds) => [...oldIds, ...newIds]);
                setLastId(newVideos[newVideos.length-1].id);
            }
        } catch (error) {
            console.error("Error fetching videos:", error);
        } finally {
            setLoading(false);
        }
    }, [loading, lastId, ids]);

    // Créer le contenu mixte avec fallback sur AdInterstitial
    const createMixedContent = useCallback(() => {
        const mixedContent = [];
        let adsInserted = 0;
        let fallbacksThisRound = 0;
        
        posts.forEach((video, index) => {
            // Ajouter la vidéo
            mixedContent.push({
                type: 'video',
                data: video,
                key: `video-${video.id}`,
                originalIndex: index
            });
            
            // Vérifier s'il faut ajouter une pub APRÈS cette vidéo (toutes les 3 vidéos)
            const shouldAddAd = (index + 1) % 3 === 0;
            
            if (shouldAddAd) {
                // PRIORITÉ 1: Essayer d'utiliser une pub VAST si disponible
                if (adManager.availableAds.length > 0) {
                    const vastAd = adManager.availableAds[adsInserted % adManager.availableAds.length];
                    
                    mixedContent.push({
                        type: 'vast_ad',
                        data: vastAd,
                        key: `vast-ad-${vastAd.id}-${index}`,
                        originalIndex: index,
                        insertionCount: adsInserted
                    });
                } else {
                    // PRIORITÉ 2: Fallback sur publicité interstitielle
                    const interstitialId = `interstitial-${index}-${adsInserted}`;
                    
                    mixedContent.push({
                        type: 'interstitial_ad',
                        data: {
                            id: interstitialId,
                            fallbackReason: 'no_vast_ads',
                            insertionPoint: index + 1,
                            timestamp: Date.now()
                        },
                        key: `interstitial-ad-${index}-${adsInserted}`,
                        originalIndex: index,
                        insertionCount: adsInserted
                    });
                    
                    fallbacksThisRound++;
                }
                
                adsInserted++;
            }
        });
        
        // Mettre à jour les stats de fallback SEULEMENT s'il y en a de nouveaux
        if (fallbacksThisRound > 0) {
            setFallbackStats(prev => ({
                ...prev,
                totalFallbacks: prev.totalFallbacks + fallbacksThisRound,
                lastFallbackTime: Date.now()
            }));
        }
        
        return mixedContent;
    }, [posts, adManager.availableAds.length]);

    // Mettre à jour contentItems SEULEMENT quand nécessaire
    useEffect(() => {
        if (posts.length > 0) {
            const newContent = createMixedContent();
            
            setContentItems(prevContent => {
                const prevKeys = prevContent.map(item => item.key).sort();
                const newKeys = newContent.map(item => item.key).sort();
                
                const keysChanged = prevKeys.length !== newKeys.length || 
                                   prevKeys.some((key, index) => key !== newKeys[index]);
                
                if (keysChanged || prevContent.length === 0) {
                    return newContent;
                }
                
                return prevContent;
            });
        }
    }, [posts.length, adManager.availableAds.length]);

    // Observer les éléments quand le contenu change
    useEffect(() => {
        // Nettoyer les anciens observers
        visibilityObserversRef.current.forEach((observer, key) => {
            observer.disconnect();
        });
        visibilityObserversRef.current.clear();
        setVisibilityMap(new Map());

        // Créer les observers pour les nouveaux éléments après un délai
        const timeoutId = setTimeout(() => {
            contentItems.forEach((item, index) => {
                const element = document.querySelector(`[data-content-key="${item.key}"]`);
                if (element) {
                    observeElementVisibility(element, item.key);
                }
            });
        }, 100);

        return () => {
            clearTimeout(timeoutId);
        };
    }, [contentItems, observeElementVisibility]);

    // Pré-charger des pubs dès le démarrage
    useEffect(() => {
        if (adManager.availableAds.length === 0 && !adManager.isPreloading) {
            const timeout = setTimeout(() => {
                adManager.preloadAds();
            }, 1000);
            
            return () => clearTimeout(timeout);
        }
    }, [adManager.availableAds.length, adManager.isPreloading, adManager.preloadAds]);

    // Charger les vidéos initiales
    useEffect(() => {
        if (posts.length === 0 && !loading) {
            getVideos();
        }
    }, []);

    // Infinite scroll observer
    useEffect(() => {
        if (observerRef.current) {
            observerRef.current.disconnect();
        }
        
        if (contentItems.length === 0) return;

        observerRef.current = new IntersectionObserver(
            (entries) => {
                if (entries[0].isIntersecting && !loading) {
                    console.log('🔄 Infinite scroll déclenché - chargement nouvelles vidéos');
                    getVideos();
                }
            },
            {
                root: null,
                rootMargin: '200px', // Augmenté pour mobile
                threshold: 0.1,
            }
        );

        // CORRECTION : Observer plusieurs éléments pour plus de robustesse
        // Observer les 3 dernières VRAIES VIDÉOS (pas les pubs)
        const videoItems = contentItems.filter(item => item.type === 'video');
        const lastVideoIndices = [];
        
        // Trouver les indices des 3 dernières vidéos dans contentItems
        for (let i = contentItems.length - 1; i >= 0 && lastVideoIndices.length < 3; i--) {
            if (contentItems[i].type === 'video') {
                lastVideoIndices.push(i);
            }
        }
        
        console.log('📊 Infinite scroll setup:', {
            totalItems: contentItems.length,
            videoItems: videoItems.length,
            lastVideoIndices,
            loading
        });
        
        // Observer ces éléments
        lastVideoIndices.forEach(index => {
            const targetElement = document.querySelector(`[data-content-key="${contentItems[index].key}"]`);
            if (targetElement) {
                observerRef.current.observe(targetElement);
                console.log(`👁️ Observer ajouté sur vidéo index ${index} (${contentItems[index].key})`);
            } else {
                console.warn(`⚠️ Élément non trouvé pour index ${index}`);
            }
        });
        
        // Fallback : si on n'a pas trouvé d'éléments vidéo, observer le dernier élément global
        if (lastVideoIndices.length === 0) {
            console.log('🔄 Fallback : observer le dernier élément global');
            const lastIndex = contentItems.length - 1;
            const fallbackElement = document.querySelector(`[data-content-key="${contentItems[lastIndex].key}"]`);
            if (fallbackElement) {
                observerRef.current.observe(fallbackElement);
            }
        }

        return () => {
            if (observerRef.current) {
                observerRef.current.disconnect();
            }
        };
    }, [contentItems.length, getVideos, loading]); 

    // Navigation
    const goBack = useCallback(() => {
        console.log("Back button pushed");
        navigate("/");
    }, [navigate]);

    // Gestionnaires des événements publicitaires (VAST et Interstitiel)
    const handleVastAdComplete = useCallback((adId) => {
        const adKey = contentItems.find(item => item.type === 'vast_ad' && item.data.id === adId)?.key;
        if (adKey) {
            cleanupObserver(adKey);
        }
        
        adManager.getAvailableAd();
        
        if (adManager.availableAds.length < 2 && !adManager.isPreloading) {
            setTimeout(() => {
                adManager.preloadAds();
            }, 2000);
        }
    }, [adManager, contentItems, cleanupObserver]);

    const handleVastAdSkip = useCallback((adId) => {
        const adKey = contentItems.find(item => item.type === 'vast_ad' && item.data.id === adId)?.key;
        if (adKey) {
            cleanupObserver(adKey);
        }
        
        adManager.getAvailableAd();
        
        if (adManager.availableAds.length < 2 && !adManager.isPreloading) {
            setTimeout(() => {
                adManager.preloadAds();
            }, 2000);
        }
    }, [adManager, contentItems, cleanupObserver]);

    // Gestionnaire pour les publicités interstitielles
    const handleInterstitialAdComplete = useCallback((adId) => {
        const adKey = contentItems.find(item => item.type === 'interstitial_ad' && item.data.id === adId)?.key;
        if (adKey) {
            cleanupObserver(adKey);
        }
        
        setFallbackStats(prev => ({
            ...prev,
            successfulFallbacks: prev.successfulFallbacks + 1
        }));
        
        if (adManager.availableAds.length === 0 && !adManager.isPreloading) {
            setTimeout(() => {
                adManager.preloadAds();
            }, 3000);
        }
    }, [contentItems, cleanupObserver, adManager.availableAds.length, adManager.isPreloading, adManager.preloadAds]);

    // Callback pour observer les éléments DOM après leur rendu
    const elementRefCallback = useCallback((element, key) => {
        if (element) {
            setTimeout(() => {
                observeElementVisibility(element, key);
            }, 50);
        }
    }, [observeElementVisibility]);

    // Nettoyage au démontage
    useEffect(() => {
        return () => {
            // Nettoyer tous les observers au démontage
            visibilityObserversRef.current.forEach((observer) => {
                observer.disconnect();
            });
            visibilityObserversRef.current.clear();
        };
    }, []);

    return (
        <>
            <LiberiumHead>
                <title>Peuple Qc : Le 1er réseau social du Québec</title>
                <meta name="description" content="Nous sommes le 1er réseau social du Québec" />
                <meta name="viewport" content="width=device-width, initial-scale=1" />
                <link rel="icon" href="/favicon.ico" />
            </LiberiumHead>
            
            <div className="videoApp" ref={videoContainerRef}>
                {/* Bouton retour */}
                <div className="backBtn" onClick={goBack}>
                    <FaArrowLeft size={30} />
                </div>
                
                {/* Container des vidéos et publicités */}
                <div className="app__videos">
                    {contentItems.map((item, index) => {
                        const isElementVisible = visibilityMap.get(item.key) || false;
                        
                        if (item.type === 'video') {
                            return (
                                <div 
                                    key={item.key}
                                    data-content-key={item.key}
                                    ref={(el) => elementRefCallback(el, item.key)}
                                >
                                    <TiktokVideo
                                        user={user}
                                        post_id={item.data.id}
                                        video={item.data}
                                        url={item.data.postFile}
                                        seeingVideo={() => {}}
                                        isAdmin={false}
                                        adHeight={0}
                                    />
                                </div>
                            );
                        } else if (item.type === 'vast_ad') {
                            return (
                                <div 
                                    key={item.key}
                                    data-content-key={item.key}
                                    ref={(el) => elementRefCallback(el, item.key)}
                                >
                                    <VastAdPlayer
                                        adData={item.data}
                                        isVisible={isElementVisible}
                                        onAdComplete={() => handleVastAdComplete(item.data.id)}
                                        onAdSkip={() => handleVastAdSkip(item.data.id)}
                                        adHeight={0}
                                        className="video"
                                        fireTracking={adManager.fireTracking}
                                    />
                                </div>
                            );
                        } else if (item.type === 'interstitial_ad') {
                            return (
                                <div 
                                    key={item.key}
                                    data-content-key={item.key}
                                    ref={(el) => elementRefCallback(el, item.key)}
                                >
                                    <TiktokInterstitialAd
                                        isVisible={isElementVisible}
                                        onAdComplete={() => handleInterstitialAdComplete(item.data.id)}
                                        onAdSkip={() => handleInterstitialAdComplete(item.data.id)}
                                        adHeight={0}
                                        className="video"
                                    />
                                </div>
                            );
                        }
                        return null;
                    })}
                    
                    {/* Indicateur de chargement */}
                    {loading && (
                        <div className="loading-indicator">
                            <div className="spinner"></div>
                        </div>
                    )}
                </div>
            </div>
        </>
    );
};

export { Tiktok };
export default Tiktok;